import React, { SetStateAction } from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  DatePicker,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Progress,
} from 'web-library';
import ApiButton from 'web-library/stories/atom/Button/ApiButton';
import { useGetWithdrawalUniqueLinkDetails } from '../../../api-refactored/unique-link/useGetUniqueLinkDetails';
import { useCustomSelectDropdown } from '../../manageAnnouncements/CreateNewAnnouncement/useCreateEditAnnouncement';
import { usePostInvalidateUniqueLink } from '../../../api-refactored/unique-link/usePostInvalidateUniqueLink';
import dayjs from 'dayjs';

const useStyles = makeStyles()((theme) => ({
  selectContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    maxWidth: '960px',
  },
  cancelButton: {
    marginRight: theme.spacing(2),
  },
  rootContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(6),
    padding: '0 25px 0 16px !important',
  },
  dialog: {
    overflow: 'hidden',
  },
  chipsContainer: {
    overflowX: 'hidden',
    width: '100%',
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  iconButton: {
    position: 'absolute',
    right: '45px',
    top: 'calc(50% - 12px)',
    padding: 0,
  },
  chipLabel: {
    padding: 0,
  },
  arrowIconContainer: {
    right: '-10px',
  },
}));

interface UniqueLinkDetailsModalContentProps {
  uniqueLinkToken?: string;
  setSelectedUniqueLinkToken: React.Dispatch<SetStateAction<string | undefined>>;
  refetchUniqueLinkListing: () => void;
  disableInvalidate: boolean;
}

const UniqueLinkDetailsModal = ({
  uniqueLinkToken,
  setSelectedUniqueLinkToken,
  refetchUniqueLinkListing,
  disableInvalidate,
}: UniqueLinkDetailsModalContentProps) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const { data: uniqueLinkDetails, isLoading } = useGetWithdrawalUniqueLinkDetails({
    token: uniqueLinkToken,
    enabled: !!uniqueLinkToken,
  });

  const { mutate: invalidateUniqueLinkMutate, status: invalidateUniqueLinkApiStatus } =
    usePostInvalidateUniqueLink({
      onSuccess: () => {
        setSelectedUniqueLinkToken(undefined);
        refetchUniqueLinkListing();
      },
    });

  const { handleMenuItemsRender } = useCustomSelectDropdown({
    classes,
  });

  const handleCloseUniqueLinkDetailsModal = () => {
    setSelectedUniqueLinkToken(undefined);
  };

  const handleInvalidateUniqueLink = () => {
    if (uniqueLinkToken) {
      invalidateUniqueLinkMutate(uniqueLinkToken);
    }
  };

  if (isLoading) {
    return <Progress />;
  }
  return (
    <Dialog
      id="unique-link-listing-modal-dialog"
      fullWidth
      open={!!uniqueLinkToken}
      maxWidth="lg"
      slotProps={{ paper: { className: classes.dialog } }}
    >
      <DialogTitle id="unique-link-details-modal-title">
        {t('admin->>UniqueLink.UniqueLinkDetailsModalTitle')}
      </DialogTitle>
      <DialogContent id="unique-link-details-modal-content">
        <DatePicker
          label={t('admin->>UniqueLink.ExpiryDateLabel')}
          value={dayjs(uniqueLinkDetails?.expiryDate)}
          // className={classes.input}
          id="input-text-expiryDate"
          // @ts-ignore
          disabled
          onChange={() => {}}
          name="expiryDate"
          inputVariant="filled"
        />
      </DialogContent>
      <DialogActions>
        <Box className={classes.buttonContainer}>
          <Button
            id="button-close-unique-link-details-modal"
            type="button"
            className={classes.cancelButton}
            variant="text"
            onClick={handleCloseUniqueLinkDetailsModal}
          >
            {t('admin->>UniqueLink.Close')}
          </Button>
          <ApiButton
            status={invalidateUniqueLinkApiStatus}
            type="button"
            disabled={disableInvalidate}
            onClick={handleInvalidateUniqueLink}
            id="invalidate-unique-link-button"
          >
            {t('admin->>UniqueLink.Invalidate')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
export default UniqueLinkDetailsModal;
